import { UploadOutlined } from '@ant-design/icons';
import type { HqApi } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import {
  App,
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Slider,
  Space,
  Typography,
  Upload,
  type FormInstance
} from 'antd';
import { useCallback, useRef, useState } from 'react';

export interface LogoFieldProps {
  /** 各端注入的接口客户端（共享包不读端内 session）。 */
  hq: HqApi;
  form: FormInstance;
  /** 字段名，默认 logoUrl。 */
  name?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** 资源服务的业务类型标记（入库归类用）。 */
  bizType?: string;
}

/** 正方形边长下限 / 上限（像素）：机构 LOGO、产品 LOGO、用户头像统一口径。 */
const MIN_SIDE = 240;
const MAX_SIDE = 840;
/** 裁剪框在弹窗中的展示边长（CSS 像素，仅交互用，不影响输出分辨率）。 */
const CROP_BOX = 280;
/** 默认输出边长：落在 240–840 中间，兼顾清晰度与体积。 */
const DEFAULT_OUT = 512;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** 是否为「已符合条件的正方形」：宽高近似相等且边长落在 240–840。 */
const isQualifyingSquare = (w: number, h: number) =>
  Math.abs(w - h) <= 1 && w >= MIN_SIDE && w <= MAX_SIDE;

interface PendingImage {
  file: File;
  url: string;
  img: HTMLImageElement;
  w: number;
  h: number;
  qualifies: boolean;
}

/**
 * 图片上传（LOGO / 头像）：强制输出 240–840 像素的正方形图片。
 * - 选择图片后弹出裁剪框，可拖动、缩放，导出正方形再上传资源服务（Hq.Res.Api）；
 * - 若原图已是 240–840 的正方形，允许「直接使用」跳过裁剪；
 * - 同时保留直接粘贴外部图片地址的能力，便于导入存量资料。
 */
export function LogoField({
  hq,
  form,
  name = 'logoUrl',
  label = 'LOGO',
  required = true,
  disabled,
  bizType = 'ORG_LOGO'
}: LogoFieldProps) {
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);
  const value = Form.useWatch(name, form) as string | undefined;

  const [pending, setPending] = useState<PendingImage | null>(null);
  const [open, setOpen] = useState(false);
  // 原图已符合正方形，但用户主动选择「改为裁剪」时置为 true
  const [forceCrop, setForceCrop] = useState(false);
  // 裁剪交互状态
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [minScale, setMinScale] = useState(1);
  const [maxScale, setMaxScale] = useState(4);
  const dragRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const resetTransform = useCallback((img: PendingImage) => {
    // 裁剪框铺满（cover）所需的最小缩放：短边映射到 CROP_BOX
    const ms = CROP_BOX / Math.min(img.w, img.h);
    const mx = ms * 5;
    const s = clamp(ms, ms, mx);
    setMinScale(ms);
    setMaxScale(mx);
    setScale(s);
    setTx((CROP_BOX - img.w * s) / 2);
    setTy((CROP_BOX - img.h * s) / 2);
  }, []);

  const closeCrop = () => {
    setPending((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setOpen(false);
    setForceCrop(false);
    dragRef.current = null;
  };

  const doUpload = async (file: Blob, fileName: string) => {
    setUploading(true);
    try {
      const dto = await hq.file.upload({ file, fileName, isPublic: true, bizType });
      form.setFieldValue(name, dto.fileUrl);
      message.success(`${label}已上传`);
      closeCrop();
    } catch (err) {
      message.error(err instanceof ApiError ? err.friendlyMessage : '上传失败，请重试或直接填写图片地址');
    } finally {
      setUploading(false);
    }
  };

  // 选择文件：读取尺寸，判定是否已符合正方形要求，打开裁剪弹窗
  const pickFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('请选择图片文件');
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (!w || !h) {
        URL.revokeObjectURL(url);
        message.error('无法读取图片尺寸');
        return;
      }
      const next: PendingImage = { file, url, img, w, h, qualifies: isQualifyingSquare(w, h) };
      // 释放上一张（若有）
      setPending((prev) => {
        if (prev) URL.revokeObjectURL(prev.url);
        return next;
      });
      resetTransform(next);
      setForceCrop(false);
      setOpen(true);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      message.error('图片解析失败，请更换文件');
    };
    img.src = url;
  };

  // 缩放时保持画面中心不越界
  const onZoom = (next: number) => {
    if (!pending) return;
    const s = clamp(next, minScale, maxScale);
    const cx = tx + CROP_BOX / 2;
    const cy = ty + CROP_BOX / 2;
    setScale(s);
    setTx(clamp(cx - (pending.w * s) / 2, CROP_BOX - pending.w * s, 0));
    setTy(clamp(cy - (pending.h * s) / 2, CROP_BOX - pending.h * s, 0));
  };

  const onDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, tx, ty };
  };

  const onDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = dragRef.current;
    if (!start || !pending) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    setTx(clamp(start.tx + dx, CROP_BOX - pending.w * scale, 0));
    setTy(clamp(start.ty + dy, CROP_BOX - pending.h * scale, 0));
  };

  const onDragEnd = () => {
    dragRef.current = null;
  };

  // 原图已符合：直接上传
  const useOriginal = () => {
    if (!pending) return;
    void doUpload(pending.file, pending.file.name || `${bizType}.png`);
  };

  // 裁剪为正方形并上传（输出边长恒定落在 240–840）
  const cropAndUpload = () => {
    if (!pending) return;
    const srcSide = CROP_BOX / scale;
    const outSide = clamp(Math.round(srcSide), MIN_SIDE, MAX_SIDE);
    const canvas = document.createElement('canvas');
    canvas.width = outSide;
    canvas.height = outSide;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      message.error('浏览器不支持图片裁剪');
      return;
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(pending.img, -tx / scale, -ty / scale, srcSide, srcSide, 0, 0, outSide, outSide);

    const isJpg = pending.file.type === 'image/jpeg';
    const mime = isJpg ? 'image/jpeg' : 'image/png';
    const fileName = `${bizType.toLowerCase()}-${outSide}x${outSide}.${isJpg ? 'jpg' : 'png'}`;
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          message.error('裁剪导出失败，请重试');
          return;
        }
        void doUpload(blob, fileName);
      },
      mime,
      0.92
    );
  };

  return (
    <>
      <Space size={16} align="start" style={{ width: '100%' }}>
        <Form.Item
          name={name}
          label={label}
          rules={required ? [{ required: true, message: `请上传或填写${label}地址` }] : []}
          style={{ flex: 1, minWidth: 320 }}
          extra={`正方形，边长 ${MIN_SIDE}–${MAX_SIDE}px（${label}用于列表、单据抬头与门户展示；上传后可裁剪）`}
        >
          <Input disabled={disabled} maxLength={255} placeholder="上传后自动填入，也可粘贴图片地址" allowClear />
        </Form.Item>
        <Form.Item label=" ">
          <Space>
            <Upload
              accept="image/*"
              showUploadList={false}
              disabled={disabled || uploading}
              beforeUpload={(file) => {
                pickFile(file as File);
                return false; // 阻止自动上传，改由裁剪后手动提交
              }}
            >
              <Button icon={<UploadOutlined />} loading={uploading} disabled={disabled}>
                上传图片
              </Button>
            </Upload>
            {value ? (
              <img
                src={value}
                alt="logo"
                style={{ height: 40, width: 40, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4 }}
              />
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                未设置
              </Typography.Text>
            )}
          </Space>
        </Form.Item>
      </Space>

      <Modal
        title={`裁剪${label}（正方形 ${MIN_SIDE}–${MAX_SIDE}px）`}
        open={open}
        onCancel={closeCrop}
        width={CROP_BOX + 96}
        destroyOnHidden
        footer={
          pending && pending.qualifies && !forceCrop
            ? [
                <Button key="cancel" onClick={closeCrop} disabled={uploading}>
                  取消
                </Button>,
                <Button key="crop" onClick={() => pending && setForceCrop(true)} disabled={uploading}>
                  改为裁剪
                </Button>,
                <Button key="use" type="primary" loading={uploading} onClick={useOriginal}>
                  直接使用（已符合正方形）
                </Button>
              ]
            : [
                <Button key="cancel" onClick={closeCrop} disabled={uploading}>
                  取消
                </Button>,
                <Button key="ok" type="primary" loading={uploading} onClick={cropAndUpload}>
                  裁剪并上传
                </Button>
              ]
        }
      >
        {pending ? (
          pending.qualifies && !forceCrop ? (
            <Space direction="vertical" size={12} style={{ width: '100%' }} align="center">
              <Alert
                type="success"
                showIcon
                style={{ width: '100%' }}
                message={`原图为 ${pending.w}×${pending.h} 正方形，已满足 ${MIN_SIDE}–${MAX_SIDE}px 要求`}
                description="可直接使用，也可选择「改为裁剪」调整构图。"
              />
              <img
                src={pending.url}
                alt="preview"
                style={{
                  width: CROP_BOX,
                  height: CROP_BOX,
                  objectFit: 'contain',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  background: '#fafafa'
                }}
              />
            </Space>
          ) : (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                原图 {pending.w}×{pending.h}（非正方形或超出范围）。拖动图片调整位置，滑动缩放，导出为正方形。
              </Typography.Text>
              <div
                onPointerDown={onDragStart}
                onPointerMove={onDragMove}
                onPointerUp={onDragEnd}
                onPointerLeave={onDragEnd}
                style={{
                  position: 'relative',
                  width: CROP_BOX,
                  height: CROP_BOX,
                  margin: '0 auto',
                  overflow: 'hidden',
                  touchAction: 'none',
                  cursor: 'move',
                  userSelect: 'none',
                  background: '#f5f5f5',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9'
                }}
              >
                <img
                  src={pending.url}
                  alt="crop"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: tx,
                    top: ty,
                    width: pending.w * scale,
                    height: pending.h * scale,
                    maxWidth: 'none',
                    pointerEvents: 'none'
                  }}
                />
                {/* 三分构图辅助线 */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    boxShadow: 'inset 0 0 0 1px rgba(24,144,255,.6)'
                  }}
                >
                  <div style={{ position: 'absolute', left: '33.33%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,.5)' }} />
                  <div style={{ position: 'absolute', left: '66.66%', top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,.5)' }} />
                  <div style={{ position: 'absolute', top: '33.33%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,.5)' }} />
                  <div style={{ position: 'absolute', top: '66.66%', left: 0, right: 0, height: 1, background: 'rgba(255,255,255,.5)' }} />
                </div>
              </div>
              <div style={{ padding: '0 8px' }}>
                <Slider
                  min={minScale}
                  max={maxScale}
                  step={(maxScale - minScale) / 100 || 0.01}
                  value={scale}
                  onChange={onZoom}
                  tooltip={{ open: false }}
                />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center', display: 'block' }}>
                将导出约 {clamp(Math.round(CROP_BOX / scale), MIN_SIDE, MAX_SIDE)}×
                {clamp(Math.round(CROP_BOX / scale), MIN_SIDE, MAX_SIDE)}px（默认 {DEFAULT_OUT}px）
              </Typography.Text>
            </Space>
          )
        ) : null}
      </Modal>
    </>
  );
}

export default LogoField;
